import React, { useState, useEffect } from 'react';
import slugify from 'slugify';
import { useStaticQuery, graphql } from 'gatsby';
import Utils from 'gatsby-theme-carbon/src/components/Utils';
import Layout from 'gatsby-theme-carbon/src/components/Layout';
import EditLink from 'gatsby-theme-carbon/src/components/EditLink';
import CustomPageHeader from '../components/CustomPageHeader';
import FeedbackSection from '../components/FeedbackSection';
import PageTabs from 'gatsby-theme-carbon/src/components/PageTabs';
import Main from 'gatsby-theme-carbon/src/components/Main';
import useMetadata from 'gatsby-theme-carbon/src/util/hooks/useMetadata';
import LastModifiedDate from 'gatsby-theme-carbon/src/components/LastModifiedDate';
import { ThemeProvider, theme as defaultTheme } from 'legion-ui';
import MultiBrandSection from '../components/MultiBrandSection';
import Toc from '../components/Toc';
import { tribes, release } from './Constant';
import NewRelease from '../components/NewRelease';
import NextPrev from '../components/NextPrevious';

const Default = ({ pageContext, children, location, Title, Description }) => {
  const { frontmatter = {}, relativePagePath, titleType } = pageContext;
  const [activeTheme, setActiveTheme] = useState(tribes[0]);
  const [newRelease, setNewRelease] = useState('');

  const {
    tabs,
    title,
    theme: frontmatterTheme,
    description,
    image,
    label,
    keywords,
    date,
    multiBrand,
    toc,
  } = frontmatter;

  useEffect(() => {
    // Get stored value
    const savedTheme = JSON.parse(localStorage.getItem('theme'));
    if (savedTheme) {
      setActiveTheme(savedTheme);
    } else {
      setActiveTheme({ value: 'Default', label: 'Default Theme', content: defaultTheme });
    }
  }, []);

  const handleChangeTheme = (event) => {
    setActiveTheme(event);
    localStorage.setItem('theme', JSON.stringify(event));
  };

  const { interiorTheme } = useMetadata();

  // get the path prefix if it exists
  const {
    site: { pathPrefix },
  } = useStaticQuery(graphql`
    query pathPrefixQueryAndPathPrefixQuery {
      site {
        pathPrefix
      }
      site {
        pathPrefix
      }
    }
  `);

  // let gatsby handle prefixing
  const slug = pathPrefix ? location.pathname.replace(pathPrefix, '') : location.pathname;

  const getCurrentTab = () => {
    if (!tabs) return '';
    return slug.split('/').filter(Boolean).slice(-1)[0] || slugify(tabs[0], { lower: true });
  };

  useEffect(() => {
    setNewRelease(localStorage.getItem('newRelease') || 'test');
  }, []);

  const currentTab = getCurrentTab();

  const theme = frontmatterTheme || interiorTheme;

  return (
    <Layout
      tabs={tabs}
      homepage={false}
      theme={theme}
      pageTitle={title}
      pageDescription={description}
      pageKeywords={keywords}
      titleType={titleType}
    >
      <CustomPageHeader
        title={Title ? <Title /> : title}
        description={Description ? <Description /> : description}
        image={image}
        label={label}
      />

      {newRelease && release.keyNewRelease !== newRelease && title === 'Let’s start with Legion' && (
        <NewRelease release={release.keyNewRelease} setNewRelease={setNewRelease} />
      )}

      {multiBrand === true ? (
        <ThemeProvider theme={activeTheme.content}>
          <MultiBrandSection tribes={tribes} activeTheme={activeTheme} handleChangeTheme={handleChangeTheme} />
          {tabs && <PageTabs title={title} slug={slug} tabs={tabs} currentTab={currentTab} />}
          <div className={`flex ${toc && 'content'}`}>
            <Main padded>
              {children}
              <EditLink relativePagePath={relativePagePath} />
              <LastModifiedDate date={date} />
              <NextPrev />
            </Main>
            {toc && (
              <div className="toc">
                <Toc item={toc} tabs={tabs} />
              </div>
            )}
          </div>
        </ThemeProvider>
      ) : (
        <React.Fragment>
          {tabs && <PageTabs title={title} slug={slug} tabs={tabs} currentTab={currentTab} />}
          <div className={`flex ${toc && 'content'}`}>
            <Main padded>
              {children}
              <EditLink relativePagePath={relativePagePath} />
              <LastModifiedDate date={date} />
              <NextPrev />
            </Main>

            {toc && (
              <div className="toc">
                <Toc item={toc} tabs={tabs} />
              </div>
            )}
          </div>
        </React.Fragment>
      )}
      <Utils />
      <FeedbackSection toc={toc} />
    </Layout>
  );
};

export default Default;
